import { useEffect, useRef, useState } from 'react';
import './PhoneNumberInput.scss';

type Parts = [string, string, string];

type PhoneNumberInputProps = {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  className?: string;
  groupClassName?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  required?: boolean;
  autoComplete?: string;
  /** 각 입력창의 최대 자릿수. 기본값은 010-1234-5678 형태 기준 */
  lengths?: [number, number, number];
};

const DEFAULT_LENGTHS: [number, number, number] = [3, 4, 4];

function splitPhone(value: string, lengths: [number, number, number]): Parts {
  const digits = (value || '').replace(/\D/g, '');
  return [
    digits.slice(0, lengths[0]),
    digits.slice(lengths[0], lengths[0] + lengths[1]),
    digits.slice(lengths[0] + lengths[1], lengths[0] + lengths[1] + lengths[2]),
  ];
}

export default function PhoneNumberInput({
  value,
  onChange,
  id,
  className,
  groupClassName,
  style,
  disabled,
  required,
  autoComplete,
  lengths = DEFAULT_LENGTHS,
}: PhoneNumberInputProps) {
  const [parts, setParts] = useState<Parts>(() => splitPhone(value, lengths));
  const ref0 = useRef<HTMLInputElement>(null);
  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  const refs = [ref0, ref1, ref2];

  useEffect(() => {
    const externalDigits = (value || '').replace(/\D/g, '');
    if (externalDigits !== parts.join('')) {
      setParts(splitPhone(value, lengths));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const updatePart = (index: 0 | 1 | 2, raw: string) => {
    const digits = raw.replace(/\D/g, '');
    const next: Parts = [...parts];

    if (index === 2) {
      next[2] = digits.slice(0, lengths[2]);
    } else if (index === 1) {
      next[1] = digits.slice(0, lengths[1]);
      const overflow = digits.slice(lengths[1]);
      if (overflow) next[2] = overflow.slice(0, lengths[2]);
    } else {
      next[0] = digits.slice(0, lengths[0]);
      let overflow = digits.slice(lengths[0]);
      if (overflow) {
        next[1] = overflow.slice(0, lengths[1]);
        overflow = overflow.slice(lengths[1]);
      }
      if (overflow) next[2] = overflow.slice(0, lengths[2]);
    }

    setParts(next);
    onChange(next.filter(Boolean).join('-'));

    if (next[index].length >= lengths[index] && index < 2) {
      refs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: 1 | 2) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && parts[index] === '') {
      refs[index - 1].current?.focus();
    }
  };

  return (
    <div className={`phone-number-input ${groupClassName ?? ''}`}>
      <input
        id={id}
        ref={ref0}
        className={`phone-number-input__box ${className ?? ''}`}
        style={style}
        type="tel"
        inputMode="numeric"
        value={parts[0]}
        maxLength={lengths[0]}
        onChange={(e) => updatePart(0, e.target.value)}
        disabled={disabled}
        required={required}
        autoComplete={autoComplete}
      />
      <span className="phone-number-input__dash">-</span>
      <input
        ref={ref1}
        className={`phone-number-input__box ${className ?? ''}`}
        style={style}
        type="tel"
        inputMode="numeric"
        value={parts[1]}
        maxLength={lengths[1]}
        onChange={(e) => updatePart(1, e.target.value)}
        onKeyDown={handleKeyDown(1)}
        disabled={disabled}
      />
      <span className="phone-number-input__dash">-</span>
      <input
        ref={ref2}
        className={`phone-number-input__box ${className ?? ''}`}
        style={style}
        type="tel"
        inputMode="numeric"
        value={parts[2]}
        maxLength={lengths[2]}
        onChange={(e) => updatePart(2, e.target.value)}
        onKeyDown={handleKeyDown(2)}
        disabled={disabled}
      />
    </div>
  );
}
